import { useCallback, useState } from "react";
import { z } from "zod";
import { fetchFastApi, ApiError } from "../api/fastapi";
import { toast } from "sonner";
import { useHookCache } from "../lib/hookCache";
import { useLiveDocumentProcessingEnabled } from "./useLiveDocumentProcessingEnabled";

const ExternalFileSchema = z.object({
  id: z.number(),
  name: z.string().nullable(),
  type: z.string().nullable(),
  size: z.string().nullable(),
  key: z.string().nullable(),
  storageType: z.string().nullable(),
  status: z.string().nullable(),
  fileableType: z.string().nullable(),
  fileableId: z.number().nullable(),
});

export type ExternalFile = z.infer<typeof ExternalFileSchema>;

const ExternalFilesResponseSchema = z.object({
  files: z.array(ExternalFileSchema),
});

interface UseExternalFilesOptions {
  controlSessionId?: string | null;
  notifyDocumentUploadStarted?: () => void;
}

export function useExternalFiles(options?: UseExternalFilesOptions) {
  const { controlSessionId, notifyDocumentUploadStarted } = options ?? {};
  const { invalidate } = useHookCache();
  // When the live document-processing view is enabled it supersedes the
  // per-transfer "transferred successfully…" confirmation, so suppress the
  // redundant success toast to stop it stacking in front of the live view.
  const suppressUploadToast = useLiveDocumentProcessingEnabled();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<ExternalFile[]>([]);
  const [downloading, setDownloading] = useState<Set<number>>(new Set());

  const fetchExternalFiles = useCallback(async (controlId: string) => {
    setLoading(true);
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: `/controls/external/control/${controlId}/files`,
      });
      const parsed = ExternalFilesResponseSchema.parse(response);
      setFiles(parsed.files);
      return parsed.files;
    } catch (error) {
      console.error("Failed to fetch external files:", error);
      toast.error("Failed to fetch external files");
      setFiles([]);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadFile = useCallback(async (file: ExternalFile) => {
    setDownloading((prev) => new Set(prev).add(file.id));
    try {
      const response = await fetchFastApi({
        method: "GET",
        path: `/controls/external/files/${file.id}/download-url`,
      });
      const url = z.object({ url: z.string() }).parse(response).url;
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name ?? `file-${file.id}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download external file:", error);
      toast.error("Failed to download file");
    } finally {
      setDownloading((prev) => {
        const next = new Set(prev);
        next.delete(file.id);
        return next;
      });
    }
  }, []);

  const transferFilesToProcedures = useCallback(
    async (
      transferFiles: ExternalFile[],
      procedureSessionIds: string[],
      shouldMatchDocuments?: boolean,
    ) => {
      if (!controlSessionId) {
        console.error("Control session not found");
        toast.error("Control session not found");
        return;
      }
      try {
        await fetchFastApi({
          method: "POST",
          path: `/controls/external/files/transfer-to-procedures`,
          body: {
            controlSessionId,
            procedureSessionIds,
            files: transferFiles.map((f) => ({
              externalFileId: f.id,
              fileName: f.name,
            })),
            shouldMatchDocuments: shouldMatchDocuments ?? true,
          },
        });

        if (!suppressUploadToast) {
          toast.success(
            `${transferFiles.length} file${
              transferFiles.length > 1 ? "s" : ""
            } transferred successfully to ${procedureSessionIds.length} procedure${
              procedureSessionIds.length > 1 ? "s" : ""
            }. This may take a few seconds to process.`,
            { duration: 10000 },
          );
        }

        notifyDocumentUploadStarted?.();

        await invalidate(["control-session", controlSessionId, "status"]);
        for (const psId of procedureSessionIds) {
          await invalidate(["procedure-session", psId, "documents"]);
        }
      } catch (error) {
        console.error("Error transferring files:", error);
        // Surface the backend's specific error detail (e.g. an
        // unsupported-file message) instead of a fixed generic string.
        if (error instanceof ApiError) {
          toast.error(error.getUserMessage());
        } else {
          toast.error("Error transferring files");
        }
      }
    },
    [
      controlSessionId,
      invalidate,
      notifyDocumentUploadStarted,
      suppressUploadToast,
    ],
  );

  const transferGuidanceFilesToProcedures = useCallback(
    async (transferFiles: ExternalFile[], procedureSessionIds: string[]) => {
      if (!controlSessionId) {
        console.error("Control session not found");
        toast.error("Control session not found");
        return;
      }
      try {
        await fetchFastApi({
          method: "POST",
          path: `/controls/external/files/transfer-guidance-to-procedures`,
          body: {
            controlSessionId,
            procedureSessionIds,
            files: transferFiles.map((f) => ({
              externalFileId: f.id,
              fileName: f.name,
            })),
          },
        });

        if (!suppressUploadToast) {
          toast.success(
            `${transferFiles.length} guidance file${
              transferFiles.length > 1 ? "s" : ""
            } transferred successfully to ${procedureSessionIds.length} procedure${
              procedureSessionIds.length > 1 ? "s" : ""
            }. This may take a few seconds to process.`,
            { duration: 10000 },
          );
        }

        notifyDocumentUploadStarted?.();

        await invalidate(["control-session", controlSessionId, "status"]);
        for (const psId of procedureSessionIds) {
          await invalidate(["procedure-session", psId, "documents"]);
        }
      } catch (error) {
        console.error("Error transferring guidance files:", error);
        toast.error("Error transferring guidance files");
      }
    },
    [
      controlSessionId,
      invalidate,
      notifyDocumentUploadStarted,
      suppressUploadToast,
    ],
  );

  return {
    fetchExternalFiles,
    downloadFile,
    transferFilesToProcedures,
    transferGuidanceFilesToProcedures,
    files,
    loading,
    downloading,
  };
}
